import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { addDays } from 'date-fns';
import { filter as _filter, get as _get, uniq as _uniq } from 'lodash';
import { Observable, of } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  ANSNotificationCodes,
  ANSNotificationContent,
  ANSPayloadRequest,
  CoverageChangeCustomerTemplateDataModel,
  CustomerInfo,
  EligibilityEnum,
  EmailContent,
  EmailSubject,
  FromAddresse,
  ICancelProgressEmail,
  MessageModel,
  NamedInsured,
  NotificationRequestInternalEmail,
  QuickQuoteEmail,
  QuickQuoteEmailTemplates,
  QuickQuoteReplaceVehicleContext,
  ReplaceVehicleAnalyticsModel,
  ReplaceVehicleRequestCustomerEmail,
  RiskModel,
  TemplatedEmail
} from '@amfam/policy/models';
import { PrettyPolicyNum } from '@amfam/policy/pipes';
import { VehicleSelectCriteria } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade, EventAnalyticParams, PageAnalyticParams } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { Agent } from '@amfam/shared/utility/agent';
import {
  Addressee,
  ANSNotification,
  CommunicationService,
  CopyService,
  InternalEmail,
  MessageDetail,
  UtilService
} from '@amfam/shared/utility/shared-services';

@Injectable({ providedIn: 'root' })
export class QuickQuoteFeatureService {
  constructor(
    private copyService: CopyService,
    private utilService: UtilService,
    private analyticsFacade: AnalyticsFacade,
    private store: Store,
    private communicationService: CommunicationService,
    private policyUtilService: PolicyUtilService,
    private currencyPipe: CurrencyPipe
  ) {}

  /**
   * Eligibility Rules
   * 1. The vehicle that's being replaced must be of the vehicle type: Car/ Light Truck
   * 2. The vehicle that's being replaced must have a primary use other than Antique/Classic/Replica
   * 3. Replace a Vehicle Quote is accessible for customers in all Advance auto policy contract states, except Arizona
   */
  public getAdvancePolicyEligibility(risk: RiskModel): boolean {
    if (
      risk &&
      _get(risk, 'vehicleTypeName') === EligibilityEnum.vehicleTypeName &&
      _get(risk, 'vehicleUseDescription') !== EligibilityEnum.vehicleUseDescription
    ) {
      return true;
    }
    return false;
  }

  prepareCustomerEmail(templateCode: string, quickQuote: QuickQuoteEmail): TemplatedEmail {
    const recipientEmailAddress = _get(
      quickQuote,
      'customer.primaryEmail',
      _get(quickQuote, 'customer.accountEmail', '')
    );

    const riskInformation = `${quickQuote.existingVehicle.year} ${quickQuote.existingVehicle.make} ${quickQuote.existingVehicle.model}`;
    const newRiskInformation = `${quickQuote.newVehicle.year} ${quickQuote.newVehicle.make} ${
      quickQuote.newVehicle.model
    } ${_get(quickQuote, 'newVehicle.series', '')}`;

    const messageDetails: MessageDetail[] = [];
    messageDetails.push({ key: 'EmailAddress', value: recipientEmailAddress });
    messageDetails.push({ key: 'SubscriberKey', value: recipientEmailAddress });
    messageDetails.push({ key: 'FirstName', value: _get(quickQuote, 'customer.firstname', '') });
    messageDetails.push({ key: 'ProdId', value: _get(quickQuote, 'producer.agentId', '') });
    messageDetails.push({
      key: 'PolicyNumber',
      value: this.utilService.maskPolicyNumber(quickQuote.policyNumber)
    });
    messageDetails.push({ key: 'RiskInformation', value: riskInformation });
    messageDetails.push({
      key: 'TotalPremium',
      value: _get(quickQuote, 'existingVehicle.totalPremium', '')
    });
    messageDetails.push({
      key: 'MonthlyPremium',
      value: _get(quickQuote, 'existingVehicle.monthlyPremium', '')
    });
    messageDetails.push({
      key: 'TermDescription',
      value: _get(quickQuote, 'existingVehicle.term', '')
    });
    messageDetails.push({ key: 'NewRiskInformation', value: newRiskInformation });
    messageDetails.push({
      key: 'NewTotalPremium',
      value: _get(quickQuote, 'newVehicle.totalPremium', '')
    });
    messageDetails.push({
      key: 'NewMonthlyPremium',
      value: _get(quickQuote, 'newVehicle.monthlyPremium', '')
    });
    messageDetails.push({
      key: 'NewTermDescription',
      value: `${_get(quickQuote, 'newVehicle.term', '')} months`
    });

    return <TemplatedEmail>{
      templateCode,
      brandId: quickQuote.brandId,
      language: 'EN',
      customerIdentifier: _get(quickQuote, 'customer.customerId'),
      partnerId: quickQuote.partnerId,
      recipientAddress: recipientEmailAddress,
      messageDetails
    };
  }

  prepareSendToAgentCustomerEmail(
    templateCode: string,
    quickQuote: QuickQuoteEmail
  ): TemplatedEmail {
    const recipientEmailAddress = _get(
      quickQuote,
      'customer.primaryEmail',
      _get(quickQuote, 'customer.accountEmail', '')
    );

    const messageDetails: MessageDetail[] = [];
    messageDetails.push({ key: 'EmailAddress', value: recipientEmailAddress });
    messageDetails.push({ key: 'SubscriberKey', value: recipientEmailAddress });
    messageDetails.push({ key: 'FirstName', value: _get(quickQuote, 'customer.firstname', '') });
    messageDetails.push({ key: 'ProdId', value: _get(quickQuote, 'producer.agentId', '') });
    if (quickQuote.policyNumber)
      messageDetails.push({
        key: 'PolicyNumber',
        value: this.utilService.maskPolicyNumber(quickQuote.policyNumber)
      });

    return <TemplatedEmail>{
      templateCode,
      brandId: quickQuote.brandId,
      language: 'EN',
      customerIdentifier: quickQuote?.customer?.customerId,
      partnerId: quickQuote.partnerId,
      recipientAddress: recipientEmailAddress,
      messageDetails
    };
  }

  //** This method is used to prepare the sentToAgentEmail Template on expectation screen, Section#3*/

  prepareSendToAgentEmail(
    quickQuote: QuickQuoteEmail,
    onError = false,
    context: QuickQuoteReplaceVehicleContext = QuickQuoteReplaceVehicleContext.QUICK_QUOTE
  ): InternalEmail {
    const agent: Agent = quickQuote.producer;
    const emailAddress = this.getAgentEmail(agent);
    const email: InternalEmail = new InternalEmail();
    email.messageFrom = new Addressee(FromAddresse.ADDRESS, FromAddresse.NAME);
    email.messageToList.push(
      new Addressee(emailAddress, `${_get(agent, 'firstName', '')}  ${_get(agent, 'lastName', '')}`)
    );
    if (context === QuickQuoteReplaceVehicleContext.REPLACE_VEHICLE) {
      email.messageSubject = '';
    }
    email.messageSubject = onError
      ? EmailSubject.SEND_TO_AGENT_ONERROR
      : context === QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
        ? EmailSubject.REPLACE_VEHICLE_SEND_TO_AGENT
        : EmailSubject.SEND_TO_AGENT;
    email.messageContent = this.getEmailContent(quickQuote, agent, onError, context);
    return email;
  }

  prepareANSNotification(
    quickQuote: QuickQuoteEmail,
    notificationType: string,
    onError = false,
    context?: string
  ): ANSNotification {
    const agent: Agent = quickQuote.producer;
    const customerName = `${_get(quickQuote, 'customer.firstname', '')}  ${_get(
      quickQuote,
      'customer.lastname',
      ''
    )}`;
    const notificationText = this.getNotificationText(
      quickQuote,
      agent,
      customerName,
      onError,
      context
    );

    return <ANSNotification>{
      notificationsRequest: {
        customerName,
        notificationText,
        notificationType,
        createdBy: 'DMSI',
        policyNumber: quickQuote.policyNumber,
        referenceNumber: quickQuote.policyNumber,
        formattedReferenceNumber: new PrettyPolicyNum().transform(quickQuote.policyNumber),
        from: 'DMSI',
        dueDate: addDays(new Date(), 2).toISOString(),
        sourceSystem: 'MYACCOUNT',
        uniqueId: this.utilService.generateId()
      },
      producerId: agent.agentId
    };
  }
  //** This method is used to send email to agent, ANS and customer email */

  prepareSendToAgent(
    quickQuote: QuickQuoteEmail,
    onError = false,
    context?: QuickQuoteReplaceVehicleContext
  ): {
    agentEmail: InternalEmail;
    customerEmail: TemplatedEmail;
    ansNotification: ANSNotification;
  } {
    return {
      agentEmail: this.prepareSendToAgentEmail(quickQuote, onError, context),
      customerEmail: this.prepareSendToAgentCustomerEmail(
        context === QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
          ? QuickQuoteEmailTemplates.REPLACE_VEHICLE_SEND_TO_AGENT_EXPECTATION_SETTINGS
          : QuickQuoteEmailTemplates.REPLACE_VEHICLE_SEND_TO_AGENT,
        quickQuote
      ),
      ansNotification: this.prepareANSNotification(
        quickQuote,
        onError
          ? ANSNotificationCodes.SEND_TO_AGENT_ONERROR
          : context === QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
            ? ANSNotificationCodes.REPLACE_VEHICLE_SEND_TO_AGENT
            : ANSNotificationCodes.SEND_TO_AGENT,
        onError,
        context
      )
    };
  }

  getSendToAgentInfo(
    quickQuote: ICancelProgressEmail,
    emailTemplateId: string,
    ansCode: string
  ): {
    agentEmail: InternalEmail;
    customerEmail: TemplatedEmail;
    ansNotification: ANSNotification;
  } {
    return {
      agentEmail: this.getAgentEmailContentCancelProcess(quickQuote),
      customerEmail: this.getCustomerEmailForCancelProcess(quickQuote, emailTemplateId),
      ansNotification: this.getANSInfoCancelProcess(quickQuote, ansCode)
    };
  }

  getCustomerEmailForCancelProcess(
    quickQuote: ICancelProgressEmail,
    emailTemplateID: string
  ): TemplatedEmail {
    const customer = quickQuote.customer;

    let drivers: string;
    let coverages: string;
    let discounts: string;
    let fees: string;

    for (const vehicleNamedInsured of _uniq(
      _filter(quickQuote?.vehicle.vehicleRoles, { roleType: 'PrimaryDriver' })
    )) {
      if (drivers) {
        drivers += ', ' + _get(vehicleNamedInsured, 'name');
      } else {
        drivers = _get(vehicleNamedInsured, 'name');
      }
    }
    for (const vehicleCoverage of _uniq(_get(quickQuote?.vehicle, 'vehicleCoverages', []))) {
      if (coverages) {
        coverages += ', ' + _get(vehicleCoverage, 'name');
      } else {
        coverages = _get(vehicleCoverage, 'name');
      }
    }
    // Pull vehicle-level discounts
    for (const vehicleDiscount of _uniq(_get(quickQuote.vehicle, 'vehicleDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(vehicleDiscount, 'name');
      } else {
        discounts = _get(vehicleDiscount, 'name');
      }
    }
    // Pull policy-level discounts
    for (const policyDiscount of _uniq(_get(quickQuote, 'vehicle.policy.policyDiscounts', []))) {
      if (discounts) {
        discounts += ', ' + _get(policyDiscount, 'name');
      } else {
        discounts = _get(policyDiscount, 'name');
      }
    }
    // Pull vehicle-level fees
    for (const vehicleFee of _uniq(_get(quickQuote?.vehicle, 'vehicleFees', []))) {
      if (fees) {
        fees += ', ' + _get(vehicleFee, 'name');
      } else {
        fees = _get(vehicleFee, 'name');
      }
    }
    // Pull policy-level fees
    for (const policyFee of _uniq(_get(quickQuote, 'vehicle.policy.policyFees', []))) {
      if (fees) {
        fees += ', ' + _get(policyFee, 'name');
      } else {
        fees = _get(policyFee, 'name');
      }
    }
    for (const vehicleSurchanges of _uniq(
      _get(quickQuote, 'vehicle.policy.policySurcharges', [])
    )) {
      if (fees) {
        fees += ', ' + _get(vehicleSurchanges, 'name');
      } else {
        fees = _get(vehicleSurchanges, 'name');
      }
    }

    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('FirstName', customer?.firstname || ''));
    if (quickQuote?.policyNumber)
      messageDetails.push(
        new MessageDetail(
          'PolicyNumber',
          this.utilService.maskPolicyNumber(quickQuote.policyNumber)
        )
      );
    messageDetails.push(
      new MessageDetail('RiskInformation', this.getRiskDescription(quickQuote, true))
    );
    if (quickQuote?.existingVehicle?.totalPremium)
      messageDetails.push(
        new MessageDetail('TotalPremium', quickQuote?.existingVehicle?.totalPremium)
      );
    if (quickQuote?.existingVehicle?.monthlyPremium)
      messageDetails.push(
        new MessageDetail('MonthlyPremium', quickQuote?.existingVehicle?.monthlyPremium)
      );
    if (quickQuote?.existingVehicle?.term)
      messageDetails.push(new MessageDetail('TermDescription', quickQuote?.existingVehicle?.term));
    messageDetails.push(
      new MessageDetail('NewRiskInformation', this.getRiskDescription(quickQuote, false))
    );
    if (quickQuote?.newVehicle.totalPremium)
      messageDetails.push(
        new MessageDetail('NewTotalPremium', quickQuote?.newVehicle.totalPremium)
      );
    if (quickQuote?.newVehicle.monthlyPremium)
      messageDetails.push(
        new MessageDetail('NewMonthlyPremium', quickQuote?.newVehicle.monthlyPremium)
      );
    if (quickQuote?.newVehicle.term)
      messageDetails.push(new MessageDetail('NewTermDescription', quickQuote?.newVehicle.term));

    // Removing for the new template
    /*
        if (drivers) messageDetails.push(new MessageDetail('Drivers', drivers));
        if (quickQuote?.vehicle?.address?.addressLine1)
          messageDetails.push(
            new MessageDetail('GaragedAddress', quickQuote?.vehicle?.address?.addressLine1)
          );
        if (coverages) messageDetails.push(new MessageDetail('Coverages', coverages));
        if (discounts) messageDetails.push(new MessageDetail('Discounts', discounts));
        if (fees) messageDetails.push(new MessageDetail('Fees', fees));
    */

    return new TemplatedEmail(
      emailTemplateID,
      quickQuote.partnerId,
      'MyAccount',
      'EN',
      quickQuote.customer.customerId,
      quickQuote.customer.primaryEmail,
      messageDetails
    );
  }

  getANSInfoCancelProcess(
    quickQuote: ICancelProgressEmail,
    notificationType: string
  ): ANSNotification {
    const agent: Agent = quickQuote.producer;
    const customer: CustomerInfo = quickQuote.customer;
    const customerName = `${customer?.firstname} ${customer.lastname}`;

    const contentPath = quickQuote?.isCancelProcess
      ? 'policy.replaceVehicleCancelProcess.ANSForCancel.body'
      : 'policy.replaceVehicleCancelProcess.ANS.body';

    let notificationText = this.copyService.getCopy(contentPath, {
      agentName: `${agent?.firstName} ${agent?.lastName}`,
      customerName: quickQuote.customer.firstname + ' ' + quickQuote?.customer?.lastname,
      policyNumber: quickQuote.policyNumber,
      existingVehicle: this.getRiskDescription(quickQuote, true, true),
      newVehicle: this.getRiskDescription(quickQuote, false, true),
      msrp: this.formatMSRP(quickQuote.newVehicle?.msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(quickQuote.newVehicle?.estimatedVehiclePrice) || 'N/A',
      hasTpi: quickQuote.hasTpi || 'N/A',
      tpiName: quickQuote.tpiName || 'N/A'
    });
    if (quickQuote?.isCancelProcess && quickQuote?.orderNumber) {
      notificationText += this.copyService.getCopy(
        'policy.replaceVehicleCancelProcess.ANSForCancel.body2',
        { orderNumber: quickQuote.orderNumber }
      );
    }

    return <ANSNotification>{
      notificationsRequest: {
        customerName,
        notificationText,
        notificationType,
        createdBy: 'DMSI',
        policyNumber: quickQuote.policyNumber,
        referenceNumber: quickQuote.policyNumber,
        formattedReferenceNumber: new PrettyPolicyNum().transform(quickQuote.policyNumber),
        from: 'DMSI',
        dueDate: addDays(new Date(), 2).toISOString(),
        sourceSystem: 'MYACCOUNT',
        uniqueId: this.utilService.generateId()
      },
      producerId: agent.agentId
    };
  }

  getAgentEmailContentCancelProcess(quickQuote: ICancelProgressEmail): InternalEmail {
    const agent: Agent = quickQuote.producer;
    const emailAddress = this.getAgentEmail(agent);
    const email: InternalEmail = new InternalEmail();
    email.messageFrom = new Addressee(FromAddresse.ADDRESS, FromAddresse.NAME);
    email.messageToList.push(new Addressee(emailAddress, `${agent?.firstName} ${agent?.lastName}`));

    email.messageSubject = this.copyService.getCopy(
      'policy.replaceVehicleCancelProcess.agentEmail.subject'
    );
    const contentPath = quickQuote?.isCancelProcess
      ? 'policy.replaceVehicleCancelProcess.agentEmailForCancel.body'
      : 'policy.replaceVehicleCancelProcess.agentEmail.body';

    let body = this.copyService.getCopy(contentPath, {
      agentName: agent?.firstName,
      customerName: quickQuote.customer.firstname + ' ' + quickQuote?.customer?.lastname,
      policyNumber: quickQuote.policyNumber,
      existingVehicle: this.getRiskDescription(quickQuote, true, true),
      newVehicle: this.getRiskDescription(quickQuote, false, true),
      msrp: this.formatMSRP(quickQuote.newVehicle?.msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(quickQuote.newVehicle?.estimatedVehiclePrice) || 'N/A',
      hasTpi: quickQuote.hasTpi || 'N/A',
      tpiName: quickQuote.tpiName || 'N/A'
    });

    if (quickQuote?.isCancelProcess && quickQuote?.orderNumber) {
      body += this.copyService.getCopy(
        'policy.replaceVehicleCancelProcess.agentEmailForCancel.body2',
        { orderNumber: quickQuote.orderNumber }
      );
    }
    const footer = this.copyService.getCopy('policy.replaceVehicleCancelProcess.agentEmail.footer');
    email.messageContent = `${body}<br /> <br />${footer}`;
    return email;
  }

  getTpi(vehicles: RiskModel[], vehicleId: string): NamedInsured {
    const matchingVehicle = vehicles.find(vehicle => vehicle.id === vehicleId);
    if (matchingVehicle)
      return matchingVehicle.vehicleRoles.find(r => r.roleType === 'ThirdPartyInterest');
    return null;
  }

  private getNotificationText(
    quickQuote: QuickQuoteEmail,
    agent: Agent,
    customerName: string,
    onError: boolean,
    context?: string
  ): string {
    const ansContent = onError
      ? ANSNotificationContent.SEND_TO_AGENT_ONERROR
      : context === QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
        ? ANSNotificationContent.REPLACE_VEHICLE_SEND_TO_AGENT
        : ANSNotificationContent.SEND_TO_AGENT;
    return this.copyService.getCopy(ansContent, {
      producer: quickQuote.producer,
      model: {
        firstName: `${_get(quickQuote, 'customer.firstname', '')}`,
        lastName: `${_get(quickQuote, 'customer.lastname', '')}`
      },
      plural: '',
      agentName: `${_get(agent, 'firstName', '')} ${_get(agent, 'lastName', '')}`,
      customerName: customerName,
      policyNumber: quickQuote.policyNumber,
      existingVehicle:
        quickQuote?.existingVehicle?.year +
          ' ' +
          quickQuote?.existingVehicle?.make +
          ' ' +
          quickQuote?.existingVehicle?.model || 'N/A',
      existingVehicleVin: quickQuote.existingVehicle?.vin,
      newVehicle: quickQuote?.newVehicle
        ? quickQuote?.newVehicle?.year +
            ' ' +
            quickQuote?.newVehicle?.make +
            ' ' +
            quickQuote.newVehicle?.model || 'N/A'
        : 'N/A',
      newVehicleVin: quickQuote?.newVehicle?.vin || 'N/A',
      currentTotalPremium: !onError
        ? `$${_get(quickQuote, 'existingVehicle.monthlyPremium')}/${_get(
            quickQuote,
            'existingVehicle.term'
          )}`
        : null,
      newTotalPremium: !onError
        ? `$${_get(quickQuote, 'newVehicle.monthlyPremium')}/${_get(
            quickQuote,
            'newVehicle.term'
          )} months`
        : null,
      riskDescription: this.getRiskDescription(quickQuote, true),
      newRiskDescription: this.getRiskDescription(quickQuote, false),
      msrp: this.formatMSRP(quickQuote.newVehicle?.msrp) || 'N/A',
      estimatedVehiclePrice: this.formatMSRP(quickQuote.newVehicle?.estimatedVehiclePrice) || 'N/A'
    });
  }

  private getRiskDescription(quickQuote: QuickQuoteEmail, existingVehicle = true, getVin = false) {
    if (existingVehicle)
      return `${_get(quickQuote, 'existingVehicle.year')} ${_get(
        quickQuote,
        'existingVehicle.make'
      )} ${_get(quickQuote, 'existingVehicle.model')}${
        getVin ? ', VIN ' + quickQuote?.existingVehicle?.vin : ''
      }`;
    else
      return `${_get(quickQuote, 'newVehicle.year', 'N/A')} ${_get(
        quickQuote,
        'newVehicle.make',
        'N/A'
      )} ${_get(quickQuote, 'newVehicle.model', '')} ${_get(quickQuote, 'newVehicle.series', '')}${
        getVin ? ', VIN ' + quickQuote?.newVehicle?.vin : ''
      }`;
  }
  //**  This method is used to get the expectation email content */
  private getEmailContent(
    quickQuote: QuickQuoteEmail,
    agent: Agent,
    onError: boolean,
    context: QuickQuoteReplaceVehicleContext = QuickQuoteReplaceVehicleContext.QUICK_QUOTE
  ): string {
    if (context === QuickQuoteReplaceVehicleContext.REPLACE_VEHICLE) {
    }
    return this.copyService.getCopy(
      onError
        ? EmailContent.SEND_TO_AGENT_ONERROR
        : context === QuickQuoteReplaceVehicleContext.EXPECTATION_SETTINGS
          ? EmailContent.REPLACE_VEHICLE_SEND_TO_AGENT
          : EmailContent.SEND_TO_AGENT,
      {
        producer: quickQuote.producer,
        model: {
          firstName: `${_get(quickQuote, 'customer.firstname', '')}`,
          lastName: `${_get(quickQuote, 'customer.lastname', '')}`
        },
        plural: '',
        agentName: `${_get(agent, 'firstName', '')}  ${_get(agent, 'lastName', '')}`,
        customerName: `${_get(quickQuote, 'customer.firstname', '')} ${_get(
          quickQuote,
          'customer.lastname',
          ''
        )}`,
        policyNumber: quickQuote.policyNumber,
        existingVehicle:
          quickQuote.existingVehicle?.year +
            ' ' +
            quickQuote.existingVehicle?.make +
            ' ' +
            quickQuote.existingVehicle?.model || 'N/A',
        existingVehicleVin: quickQuote.existingVehicle?.vin,
        newVehicle: quickQuote?.newVehicle
          ? quickQuote?.newVehicle?.year +
              ' ' +
              quickQuote?.newVehicle?.make +
              ' ' +
              quickQuote?.newVehicle?.model || 'N/A'
          : 'N/A',
        newVehicleVin: quickQuote?.newVehicle?.vin || 'N/A',

        currentTotalPremium: !onError
          ? `$${_get(quickQuote, 'existingVehicle.monthlyPremium')}/${_get(
              quickQuote,
              'existingVehicle.term'
            )}`
          : null,
        newTotalPremium: !onError
          ? `$${_get(quickQuote, 'newVehicle.monthlyPremium')}/${_get(
              quickQuote,
              'newVehicle.term'
            )} months`
          : null,
        riskDescription: this.getRiskDescription(quickQuote, true),
        newRiskDescription: this.getRiskDescription(quickQuote, false),
        msrp: this.formatMSRP(quickQuote.newVehicle?.msrp) || 'N/A',
        estimatedVehiclePrice:
          this.formatMSRP(quickQuote.newVehicle?.estimatedVehiclePrice) || 'N/A'
      }
    );
  }

  private getAgentEmail(agent: Agent): string {
    const agentEmail = _get(agent, 'agencyEmails[0]', _get(agent, 'emails[0]'));
    const internalAgentEmail = _get(agent, 'agencyEmails[0]')
      ? env.policyGracePeriodNotificatonInternalAgencyMailbox
      : env.policyGracePeriodNotificatonInternal;
    return env.production ? agentEmail : internalAgentEmail;
  }

  getConfirmationData() {
    return {
      confirmation: {
        status: this.copyService.getCopy('policy.quickQuoteAgentNotificationStatus'),
        heading: this.copyService.getCopy('policy.quickQuoteAgentNotificationHeaderText')
      },
      nextSteps: {
        heading: 'Your next steps',
        text: this.copyService.getCopy('policy.quickQuoteAgentNotificationContent')
      }
    };
  }

  /**
   * @author Abhishek Singh
   * @param
   * @returns
   * @description Send the analytics for the replace a vehicle actions.
   */
  public sendAnalytics(analyticPayload: ReplaceVehicleAnalyticsModel): void {
    const pageObj: PageAnalyticParams = {
      pageName: 'MyAccount:Policies:ReplaceVehicle:' + analyticPayload.pageAnalyticName,
      experience: '',
      primaryCategory: 'MyAccount',
      subCategory1: 'Replace Vehicle',
      subCategory2: '',
      subCategory3: ''
    };
    const eventObj: EventAnalyticParams = {
      eventName: 'Replace Vehicle',
      eventStep: analyticPayload.eventName,
      replaceVehicle: {
        referenceNumber: analyticPayload.referenceNumber,
        agentName: analyticPayload.agentName
      }
    };
    if (analyticPayload.buttonAnalyticName) {
      return this.analyticsFacade.trackButtonClick({ link: analyticPayload.buttonAnalyticName });
    } else {
      if (analyticPayload.eventName) {
        return this.analyticsFacade.trackPageAndEvent(pageObj, eventObj);
      } else {
        return this.analyticsFacade.trackPage(pageObj);
      }
    }
  }

  public buildANSNotificationPayload(
    producerId: string,
    policyNumber: string,
    notificationText: string,
    orderNumber: string,
    notificationType = 'ANSMYARPVQ'
  ): Observable<ANSPayloadRequest> {
    return this.store.select(userQuery.selectUserState).pipe(
      take(1),
      mergeMap(response => {
        const ansNotificationPayload: ANSPayloadRequest = {
          notificationsRequest: {
            notificationText: notificationText,
            notificationType: notificationType,
            customerName: response.displayName,
            referenceNumber: orderNumber,
            formattedReferenceNumber: new PrettyPolicyNum().transform(policyNumber),
            policyNumber: policyNumber,
            from: 'DMSI',
            dueDate: addDays(new Date(), 2).toISOString(),
            createdBy: 'DMSI',
            uniqueId: this.utilService.generateId(),
            sourceSystem: 'MYACCOUNT'
          },
          producerId: producerId
        };
        return of(ansNotificationPayload);
      })
    );
  }

  public generateInternalEmails(model: NotificationRequestInternalEmail): Observable<any> {
    return this.sendInternalEmail(this.buildInternalEmail(model));
  }

  public buildInternalEmail(model: NotificationRequestInternalEmail) {
    const email: InternalEmail = new InternalEmail();
    const agentEmail = _get(model, 'producer.agencyEmails[0]', _get(model, 'producer.emails[0]'));

    const internalAgentEmail = _get(model, 'producer.agencyEmails[0]')
      ? env.policyGracePeriodNotificatonInternalAgencyMailbox
      : env.policyGracePeriodNotificatonInternal;

    const emailAddress = env.production ? agentEmail : internalAgentEmail;

    const firstName = _get(model, 'producer.firstName', '');
    const lastName = _get(model, 'producer.lastName', '');

    email.messageFrom = new Addressee('policy_change_request@amfam.com', 'Policy Change Request');
    email.messageToList.push(new Addressee(emailAddress, firstName + lastName));

    email.messageSubject = this.getEmailSubject(model.type);

    email.messageContent = this.generateInternalEmailContent(model);
    return email;
  }

  private getEmailSubject(typeOfEmail: NotificationRequestInternalEmail['type']): string {
    if (typeOfEmail === 'No Quote' || typeOfEmail === 'Quote to agent') {
      return `Action Needed: Your Customer Wants to Replace a Vehicle`;
    }
    return `Action Needed: Replace a Vehicle Quote from My Account`;
  }

  private sendInternalEmail(email: InternalEmail) {
    return this.communicationService.sendInternalEmail(email);
  }

  public generateInternalEmailContent(model: NotificationRequestInternalEmail): string {
    const firstName = _get(model, 'producer.firstName', '');
    const agentInformation = this.getAgentEmailBody(model.type, model);
    let content = `Dear ${firstName},<br></br>`;
    content += agentInformation;
    content += `<br></br>Thank you,<br></br>The My Account Team`;
    content += `<br></br><hr/>`;
    return content;
  }

  private getAgentEmailBody(
    typeOfEmail: NotificationRequestInternalEmail['type'],
    model: NotificationRequestInternalEmail
  ): string {
    const email = _get(model, 'email', '');
    const customerName = _get(model, 'customerName', '');
    const policyNumber = _get(model, 'policyNumber', '');
    const newVehicle = _get(model, 'newVehicle', '');
    const oldVehicle = _get(model, 'oldVehicle', '');
    const orderNumber = _get(model, 'orderNumber', '');
    const isUsedForBusiness = _get(model, 'isUsedForBusiness', '');
    const isOwner = _get(model, 'isOwner', '');
    const hasTPI = _get(model, 'hasTPI', '');
    const copyTPIIndicator = _get(model, 'copyTPIIndicator', '');
    const tpiName = _get(model, 'existingTPI', '');
    const existingTPI = hasTPI === 'Yes' ? (tpiName ? tpiName : 'Not Provided') : 'Not Applicable';
    const existingVehicleVin = _get(model, 'existingVehicleVin', '');
    const newVehicleVin = _get(model, 'newVehicleVin', '');
    const newVehicleMsrp = this.formatMSRP(model?.msrp) || 'N/A';
    const estimatedVehiclePrice = this.formatMSRP(model?.estimatedVehiclePrice) || 'N/A';

    if (typeOfEmail === 'No Quote') {
      return this.copyService.getCopy('policy.agentEmailContentNoQuote', {
        customerName,
        policyNumber,
        newVehicle,
        oldVehicle,
        isUsedForBusiness,
        isOwner,
        hasTPI,
        existingTPI,
        existingVehicleVin,
        newVehicleVin,
        newVehicleMsrp,
        estimatedVehiclePrice
      });
    }
    if (typeOfEmail === 'Quote to agent') {
      return this.copyService.getCopy('policy.agentEmailContentQuoteToAgent', {
        customerName,
        policyNumber,
        newVehicle,
        oldVehicle,
        isUsedForBusiness,
        isOwner,
        hasTPI,
        existingTPI,
        orderNumber,
        existingVehicleVin,
        newVehicleVin,
        newVehicleMsrp,
        estimatedVehiclePrice
      });
    }

    if (typeOfEmail === 'Quote' && hasTPI === 'Yes' && copyTPIIndicator === 'No') {
      return this.copyService.getCopy('policy.agentEmailContentQuoteTPIChangeNeeded', {
        customerName,
        policyNumber,
        newVehicle,
        oldVehicle,
        isUsedForBusiness,
        isOwner,
        hasTPI,
        existingTPI,
        orderNumber,
        existingVehicleVin,
        newVehicleVin,
        newVehicleMsrp,
        estimatedVehiclePrice
      });
    }
    if (
      typeOfEmail === 'Quote' &&
      ((hasTPI === 'Yes' && copyTPIIndicator === 'Yes') || hasTPI === 'No')
    ) {
      return this.copyService.getCopy('policy.agentEmailContentQuoteNoTPIChangeNeeded', {
        customerName,
        policyNumber,
        newVehicle,
        oldVehicle,
        isUsedForBusiness,
        isOwner,
        hasTPI,
        existingTPI,
        orderNumber,
        existingVehicleVin,
        newVehicleVin,
        newVehicleMsrp,
        estimatedVehiclePrice
      });
    }
    return this.copyService.getCopy('policy.agentInformation', {
      customerName: customerName,
      policyNumber: policyNumber,
      newVehicle: newVehicle,
      oldVehicle: oldVehicle,
      orderNumber: orderNumber,
      email: email
    });
  }

  public buildCustomerEmail(
    model: ReplaceVehicleRequestCustomerEmail,
    messageDetails: MessageDetail[],
    templateID: string
  ) {
    return new TemplatedEmail(
      templateID,
      model.partnerId,
      'MyAccount',
      'EN',
      model.customerId,
      model.customerEmailId,
      messageDetails
    );
  }

  public buildMessageDetails(messageModel: MessageModel) {
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('FirstName', _get(messageModel, 'firstName', '')));
    messageDetails.push(new MessageDetail('PolicyNumber', _get(messageModel, 'policyNumber', '')));

    messageDetails.push(
      new MessageDetail('RiskInformation', _get(messageModel, 'riskInformation', ''))
    );
    messageDetails.push(new MessageDetail('TotalPremium', _get(messageModel, 'totalPremium', '')));
    messageDetails.push(
      new MessageDetail('MonthlyPremium', _get(messageModel, 'monthlyPremium', ''))
    );
    messageDetails.push(
      new MessageDetail('TermDescription', _get(messageModel, 'termDescription', ''))
    );

    messageDetails.push(
      new MessageDetail('NewRiskInformation', _get(messageModel, 'newRiskInformation', ''))
    );
    messageDetails.push(
      new MessageDetail('NewTotalPremium', _get(messageModel, 'newTotalPremium', ''))
    );
    messageDetails.push(
      new MessageDetail('NewMonthlyPremium', _get(messageModel, 'newMonthlyPremium', ''))
    );
    messageDetails.push(
      new MessageDetail('NewTermDescription', _get(messageModel, 'newTermDescription', ''))
    );
    messageDetails.push(
      new MessageDetail('QuoteExpirationDate', _get(messageModel, 'quoteExpirationDate', ''))
    );

    messageDetails.push(new MessageDetail('Drivers', _get(messageModel, 'drivers', '')));
    messageDetails.push(
      new MessageDetail('GaragedAddress', _get(messageModel, 'garagedAddress', ''))
    );
    messageDetails.push(new MessageDetail('Coverages', _get(messageModel, 'coverages', '')));
    messageDetails.push(new MessageDetail('Discounts', _get(messageModel, 'discounts', '')));
    messageDetails.push(new MessageDetail('Fees', _get(messageModel, 'fees', '')));
    messageDetails.push(
      new MessageDetail('WorkOrderNumber', _get(messageModel, 'orderNumber', ''))
    );
    messageDetails.push(new MessageDetail('ProdID', _get(messageModel, 'producerId', '')));
    return messageDetails;
  }

  public buildMessageDetailsForCoverageChangeEmail(
    messageModel: CoverageChangeCustomerTemplateDataModel
  ) {
    const messageDetails: MessageDetail[] = new Array<MessageDetail>();
    messageDetails.push(new MessageDetail('FirstName', _get(messageModel, 'firstName', '')));
    messageDetails.push(new MessageDetail('PolicyNumber', _get(messageModel, 'policyNumber', '')));
    messageDetails.push(new MessageDetail('ProdID', _get(messageModel, 'ProdID', '')));
    messageDetails.push(
      new MessageDetail('CurrentCoverage', _get(messageModel, 'CurrentCoverage', ''))
    );
    messageDetails.push(
      new MessageDetail('CoverageChanges', _get(messageModel, 'CoverageChanges', ''))
    );
    messageDetails.push(new MessageDetail('Vehicle', _get(messageModel, 'Vehicle', '')));
    return messageDetails;
  }

  generateCustomerEmail(
    model: ReplaceVehicleRequestCustomerEmail,
    messageDetails: MessageDetail[],
    templateID: string
  ): Observable<any> {
    const email = new TemplatedEmail(
      templateID,
      model.partnerId,
      'MyAccount',
      'EN',
      model.customerId,
      model.customerEmailId,
      messageDetails
    );
    return this.communicationService.sendTemplatedEmail(email);
  }

  hasTPI(criteria: VehicleSelectCriteria) {
    return this.policyUtilService.getUnderWritingQuestionsDetails(
      criteria,
      'policy.newVehicleUnderWritingQuestionHasLoan',
      true
    );
  }
  private formatMSRP(msrp: string) {
    if (!msrp) {
      return '';
    }
    const value = msrp.replace(/[^0-9.-]+/g, '');
    return this.currencyPipe.transform(value, 'USD', 'symbol', '1.0-2');
  }
  private numberWithCommas(odometerReading: string) {
    if (!odometerReading) {
      return '';
    }
    const parts = odometerReading.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }
}
